/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import React from 'react';
import { setLocale } from 'yup';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { injectIntl } from 'react-intl';
import MomentUtils from '@date-io/moment';
import { Switch, Route } from 'react-router-dom';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';

import Version from 'components/Version';
import { requireAuthentication } from 'utils/authentication';
import DesignPage from 'containers/DesignPage/Loadable';
import HomePage from 'containers/HomePage/Loadable';
import LoginPage from 'containers/LoginPage/Loadable';
import NotFoundPage from 'containers/NotFoundPage/Loadable';
import StaffDetailsPage from 'containers/StaffDetailsPage/Loadable';
import StaffPage from 'containers/StaffPage/Loadable';
import VerificationPage from 'containers/VerificationPage/Loadable';
import UpcomingProjectsPage from 'containers/UpcomingProjectsPage/Loadable';
import ProjectsPage from 'containers/ProjectsPage/Loadable';
import ProjectDetailsPage from 'containers/ProjectDetailsPage/Loadable';
import TimeTrackingPage from 'containers/TimeTrackingPage/Loadable';
import CancellationsPage from 'containers/CancellationsPage/Loadable';
import ContractsPage from 'containers/ContractsPage/Loadable';
import PayrollPage from 'containers/PayrollPage/Loadable';
import ReleaseNotesPage from 'containers/ReleaseNotesPage/Loadable';
import AppointmentsPage from 'containers/AppointmentsPage/Loadable';
import FieldCategoriesPage from 'containers/FieldCategoriesPage/Loadable';
import DynamicFieldsPage from 'containers/DynamicFieldsPage/Loadable';
import TutorialDetailsPage from 'containers/TutorialDetailsPage/Loadable';
import TutorialsPage from 'containers/TutorialsPage/Loadable';
import FinesPage from 'containers/FinesPage/Loadable';

import 'moment/src/locale/de';
import timezone from 'moment-timezone';
import moment from 'moment';

import GlobalStyle from '../../global-styles';
import messages from './messages';

function App({ intl }) {
  timezone.tz.setDefault('Europe/Berlin');
  moment.locale('de');

  setLocale({
    mixed: {
      required: intl.formatMessage(messages.requiredField),
      notType: params => {
        const args = { type: params.type };
        if (params.path.toLowerCase().includes('time')) args.type = 'time';
        return intl.formatMessage(messages.notTypeField, args);
      },
    },
    number: {
      integer: intl.formatMessage(messages.integerField),
      positive: intl.formatMessage(messages.positiveField),
      min: params => intl.formatMessage(messages.minValueField, params),
      max: params => intl.formatMessage(messages.maxValueField, params),
    },
    date: {
      min: ({ min }) =>
        intl.formatMessage(messages.minDateField, { min: min.format('L') }),
      max: ({ max }) =>
        intl.formatMessage(messages.maxDateField, { max: max.format('L') }),
    },
  });

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <Helmet defaultTitle="ucm.agency" titleTemplate="%s - ucm.agency">
        <meta name="description" content="Admin" />
      </Helmet>
      <Switch>
        <Route exact path="/" component={requireAuthentication(ProjectsPage)} />
        <Route exact path="/home" component={requireAuthentication(HomePage)} />
        <Route exact path="/design" component={DesignPage} />
        <Route exact path="/login" component={LoginPage} />
        <Route exact path="/version" component={Version} />
        <Route
          exact
          path="/staff_details/:uuid/:id"
          component={requireAuthentication(StaffDetailsPage)}
        />
        <Route
          exact
          path="/staff"
          component={requireAuthentication(StaffPage)}
        />
        <Route
          exact
          path="/projects/(create)?"
          component={requireAuthentication(ProjectsPage)}
        />
        <Route
          exact
          path="/upcoming_projects"
          component={requireAuthentication(UpcomingProjectsPage)}
        />
        <Route
          exact
          path="/shift_cancellations"
          component={requireAuthentication(CancellationsPage)}
        />
        <Route
          exact
          path="/project_details/:uuid"
          component={requireAuthentication(ProjectDetailsPage)}
        />
        <Route
          exact
          path="/verification"
          component={requireAuthentication(VerificationPage)}
        />
        <Route
          exact
          path="/time_tracking"
          component={requireAuthentication(TimeTrackingPage)}
        />
        <Route
          exact
          path="/contracts"
          component={requireAuthentication(ContractsPage)}
        />
        <Route
          exact
          path="/fines"
          component={requireAuthentication(FinesPage)}
        />
        <Route
          exact
          path="/payroll"
          component={requireAuthentication(PayrollPage)}
        />
        <Route
          exact
          path="/appointments"
          component={requireAuthentication(AppointmentsPage)}
        />
        <Route
          exact
          path="/categories"
          component={requireAuthentication(FieldCategoriesPage)}
        />
        <Route
          exact
          path="/categories/:uuid/dynamic_fields"
          component={requireAuthentication(DynamicFieldsPage)}
        />
        <Route
          exact
          path="/tutorials"
          component={requireAuthentication(TutorialsPage)}
        />
        <Route
          exact
          path="/tutorial_details/:uuid"
          component={requireAuthentication(TutorialDetailsPage)}
        />
        <Route exact path="/release_notes" component={ReleaseNotesPage} />
        <Route path="" component={NotFoundPage} />
      </Switch>
      <GlobalStyle />
    </MuiPickersUtilsProvider>
  );
}

App.propTypes = {
  intl: PropTypes.object,
};

export default injectIntl(App);
